import React from "react";

import { ReactComponent as MaleArtwork } from "../../../assets/svg/form/Male.svg";
import { ReactComponent as FemaleArtwork } from "../../../assets/svg/form/female.svg";
import { ReactComponent as MaleArtworkMuted } from "../../../assets/svg/form/disabled-male.svg";
import { ReactComponent as FemaleArtworkMuted } from "../../../assets/svg/form/disabled-female.svg";

/**
 * The two glyphs the gender pair wears, on both public forms.
 *
 * Here rather than in either feature because both draw the same question: the
 * enquiry form asks a stranger and the pre-consultation form asks a client,
 * and a reader who meets the two on different days should not be able to tell
 * that they were built at different times. A second copy is how that stops
 * being true.
 *
 * The artwork is the design's own, out of `assets/svg/form`, and carries its
 * two brand fills inside the file. Both call sites still wrap the icon in a
 * `text-brand` span; that colour no longer reaches these marks, and is left
 * standing because the same span colours whatever else a choice row is given.
 *
 * Two sets, because the fills are inside the files. A disabled pair greys its
 * borders, its ground and its labels through CSS — but a mark whose teal is
 * painted into the SVG stays teal, and one bright glyph on a flat grey button
 * is the one thing left on the row still claiming to be pressable. The design
 * ships the same two marks drawn in greys for exactly that, and the muted set
 * is what a locked pair wears. See `GENDER_CHOICES_LOCKED`.
 */

/**
 * The asset is drawn at 18; the pair stands at 16 in both forms.
 *
 * Under the label beside it rather than over it. These are marks on a pair of
 * buttons whose words carry the answer — "Male", "Female" — so a glyph drawn
 * larger than the type it sits next to reads as the thing being chosen, which
 * it is not. Sixteen is the line height of the label, which is what makes the
 * two read as one row rather than as an icon with a caption.
 */
const MARK_SIZE = 16;

export const MarsMark: React.FC = () => (
  <MaleArtwork width={MARK_SIZE} height={MARK_SIZE} aria-hidden="true" />
);

export const VenusMark: React.FC = () => (
  <FemaleArtwork width={MARK_SIZE} height={MARK_SIZE} aria-hidden="true" />
);

/** The same two marks in the greys a disabled control is drawn in. */
export const MarsMarkMuted: React.FC = () => (
  <MaleArtworkMuted width={MARK_SIZE} height={MARK_SIZE} aria-hidden="true" />
);

export const VenusMarkMuted: React.FC = () => (
  <FemaleArtworkMuted width={MARK_SIZE} height={MARK_SIZE} aria-hidden="true" />
);

/** The pair, in the order both forms offer them. */
export const GENDER_CHOICES = [
  { value: "Male" as const, icon: <MarsMark /> },
  { value: "Female" as const, icon: <VenusMark /> },
];

/**
 * The same pair for a question somebody else already answered — the
 * pre-consultation form's basics step, where a client's gender is what their
 * practitioner put on file and is shown rather than asked.
 *
 * Both marks are muted, including the one on the answer that was picked. The
 * chosen button keeps its tint, which is what says *which* answer it is; the
 * mark is what would otherwise say the button is live, and on a locked row it
 * has no business saying so.
 *
 * A constant rather than a `genderChoices(locked)` call, so neither form hands
 * the control a new array on every keystroke elsewhere on the step.
 */
export const GENDER_CHOICES_LOCKED = [
  { value: "Male" as const, icon: <MarsMarkMuted /> },
  { value: "Female" as const, icon: <VenusMarkMuted /> },
];
